import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, ArrowLeft, X, ChevronLeft, ChevronRight, Play, ZoomIn } from "lucide-react";

interface Gallery {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  featured_image_url: string | null;
  gallery_type: string;
  event_date: string | null;
  venue: string | null;
  seo_title: string | null;
  seo_description: string | null;
}

interface GalleryImage {
  id: string;
  image_url: string;
  alt_text: string | null;
}

interface GalleryVideo {
  id: string;
  video_url: string;
  title: string | null;
  video_type: string | null;
}

interface RelatedGallery {
  id: string;
  title: string;
  slug: string;
  featured_image_url: string | null;
  gallery_type: string;
}

const GalleryDetail = () => {
  const { slug } = useParams();
  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [videos, setVideos] = useState<GalleryVideo[]>([]);
  const [tags, setTags] = useState<{ name: string }[]>([]);
  const [relatedGalleries, setRelatedGalleries] = useState<RelatedGallery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (slug) {
      fetchGallery();
    }
  }, [slug]);

  const fetchGallery = async () => {
    setIsLoading(true);

    const { data: galleryData } = await supabase
      .from("galleries")
      .select("*")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle();

    if (galleryData) {
      setGallery(galleryData);

      // Fetch images
      const { data: imageData } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("gallery_id", galleryData.id)
        .order("sort_order");
      setImages(imageData || []);

      // Fetch videos
      const { data: videoData } = await supabase
        .from("gallery_videos")
        .select("*")
        .eq("gallery_id", galleryData.id)
        .order("sort_order");
      setVideos(videoData || []);

      // Fetch tags
      const { data: tagData } = await supabase
        .from("gallery_tags")
        .select("tag_id")
        .eq("gallery_id", galleryData.id);

      if (tagData && tagData.length > 0) {
        const { data: tagNames } = await supabase
          .from("tags")
          .select("name")
          .in("id", tagData.map((t) => t.tag_id));
        setTags(tagNames || []);
      }

      // Fetch related galleries
      const { data: related } = await supabase
        .from("galleries")
        .select("id, title, slug, featured_image_url, gallery_type")
        .eq("is_published", true)
        .eq("gallery_type", galleryData.gallery_type)
        .neq("id", galleryData.id)
        .limit(3);
      setRelatedGalleries(related || []);
    }

    setIsLoading(false);
  };

  const getVideoEmbedUrl = (url: string) => {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("youtu.be")
        ? url.split("/").pop()
        : new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes("vimeo.com")) {
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : images.length - 1));
  const nextImage = () => setLightboxIndex((i) => (i !== null && i < images.length - 1 ? i + 1 : 0));

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (!gallery) {
    return (
      <Layout>
        <div className="section-padding text-center">
          <h1 className="font-serif text-3xl">Gallery not found</h1>
          <Button asChild className="mt-4">
            <Link to="/galleries">Back to Galleries</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{gallery.seo_title || gallery.title} - Pixie Clicks Gallery</title>
        <meta name="description" content={gallery.seo_description || gallery.description || ""} />
        <link rel="canonical" href={`https://pixieclicks.com/galleries/${gallery.slug}`} />
        <meta property="og:title" content={gallery.seo_title || gallery.title} />
        <meta property="og:description" content={gallery.seo_description || gallery.description || ""} />
        {gallery.featured_image_url && <meta property="og:image" content={gallery.featured_image_url} />}
        <meta property="og:type" content="article" />
      </Helmet>
      <Layout>
        {/* Hero */}
        {gallery.featured_image_url && (
          <div className="w-full h-[60vh] md:h-[70vh] relative">
            <img
              src={gallery.featured_image_url}
              alt={gallery.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <div className="container-wide">
                <Badge className="capitalize mb-4">{gallery.gallery_type}</Badge>
                <h1 className="font-serif text-3xl md:text-5xl font-semibold text-foreground">
                  {gallery.title}
                </h1>
                <div className="mt-4 flex flex-wrap gap-4 text-foreground/80">
                  {gallery.venue && (
                    <span className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      {gallery.venue}
                    </span>
                  )}
                  {gallery.event_date && (
                    <span className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      {format(new Date(gallery.event_date), "MMMM d, yyyy")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <section className="section-padding">
          <div className="container-wide">
            {/* Back link */}
            <Link
              to="/galleries"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Galleries
            </Link>

            {/* Description */}
            {gallery.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mb-12"
              >
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {gallery.description}
                </p>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6">
                    {tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Photo Gallery - Two Column Layout with Correct Order */}
            {images.length > 0 && (
              <div className="mb-16">
                <h2 className="font-serif text-2xl font-semibold mb-6">Photos</h2>
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Column - Even indices (0, 2, 4...) */}
                  <div className="flex-1 flex flex-col gap-6">
                    {images
                      .filter((_, index) => index % 2 === 0)
                      .map((image) => {
                        const originalIndex = images.findIndex((img) => img.id === image.id);
                        return (
                          <motion.div
                            key={image.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: originalIndex * 0.05 }}
                            className="overflow-hidden rounded-xl cursor-pointer group relative"
                            onClick={() => openLightbox(originalIndex)}
                          >
                            <img
                              src={image.image_url}
                              alt={image.alt_text || `Gallery image ${originalIndex + 1}`}
                              loading="lazy"
                              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                                <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                  <ZoomIn className="h-6 w-6 text-foreground" />
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                  {/* Right Column - Odd indices (1, 3, 5...) */}
                  <div className="flex-1 flex flex-col gap-6">
                    {images
                      .filter((_, index) => index % 2 === 1)
                      .map((image) => {
                        const originalIndex = images.findIndex((img) => img.id === image.id);
                        return (
                          <motion.div
                            key={image.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: originalIndex * 0.05 }}
                            className="overflow-hidden rounded-xl cursor-pointer group relative"
                            onClick={() => openLightbox(originalIndex)}
                          >
                            <img
                              src={image.image_url}
                              alt={image.alt_text || `Gallery image ${originalIndex + 1}`}
                              loading="lazy"
                              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                                <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                  <ZoomIn className="h-6 w-6 text-foreground" />
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* Video Gallery */}
            {videos.length > 0 && (
              <div className="mb-16">
                <h2 className="font-serif text-2xl font-semibold mb-6">Videos</h2>
                <div className="grid gap-8 md:grid-cols-2">
                  {videos.map((video) => (
                    <div key={video.id} className="aspect-video rounded-xl overflow-hidden bg-muted">
                      <iframe
                        src={getVideoEmbedUrl(video.video_url)}
                        title={video.title || "Wedding video"}
                        className="w-full h-full"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="text-center py-12 bg-muted/30 rounded-2xl">
              <h2 className="font-serif text-2xl md:text-3xl font-semibold mb-4">
                Love What You See?
              </h2>
              <p className="text-muted-foreground mb-6">
                Let's create beautiful memories for your special day too.
              </p>
              <Button variant="hero" size="lg" asChild>
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Galleries */}
        {relatedGalleries.length > 0 && (
          <section className="section-padding bg-muted/30">
            <div className="container-wide">
              <h2 className="font-serif text-2xl md:text-3xl font-semibold mb-8">
                More {gallery.gallery_type === "wedding" ? "Weddings" : "Engagements"}
              </h2>
              <div className="grid gap-8 md:grid-cols-3">
                {relatedGalleries.map((related) => (
                  <Link
                    key={related.id}
                    to={`/galleries/${related.slug}`}
                    className="group"
                  >
                    <div className="aspect-[3/4] overflow-hidden rounded-xl bg-muted">
                      {related.featured_image_url ? (
                        <img
                          src={related.featured_image_url}
                          alt={related.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <span className="text-muted-foreground">No image</span>
                        </div>
                      )}
                    </div>
                    <h3 className="mt-4 font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {related.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
              onClick={closeLightbox}
            >
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
              >
                <X className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 p-2 text-white/80 hover:text-white"
              >
                <ChevronLeft className="h-10 w-10" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 p-2 text-white/80 hover:text-white"
              >
                <ChevronRight className="h-10 w-10" />
              </button>
              <motion.img
                key={lightboxIndex}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                src={images[lightboxIndex]?.image_url}
                alt=""
                className="max-h-[90vh] max-w-[90vw] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-4 text-white/60 text-sm">
                {lightboxIndex + 1} / {images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Layout>
    </>
  );
};

export default GalleryDetail;
